/**
 * VerifyFileTab — drop a file, check its embedded proof status.
 *
 * Reads the file client-side, base64-encodes it, then POSTs to
 * /api/v1/proof/verify-file. Shows proof status and details on response.
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, CardSubtitle, CardTitle } from '@/components/Card';
import { ErrorState } from '@/components/ErrorState';
import { HashDisplay } from '@/components/HashDisplay';
import { Spinner } from '@/components/LoadingState';
import { ProofStatusBadge } from '@/components/ProofStatusBadge';
import type { ProofStatus } from '@/components/ProofStatusBadge';
import { cn } from '@/utils/cn';

// ── Types ─────────────────────────────────────────────────────────

interface EmbeddedProof {
  signer: string;
  timestamp: string;
  sequence: number;
  engine_sig: string;
}

interface VerifyFileResponse {
  status: ProofStatus;
  proof: EmbeddedProof | null;
  canonical_hash: string;
}

// ── Helpers ───────────────────────────────────────────────────────

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1] ?? '');
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ── Drop zone (file-only variant — accepts any file type) ─────────

function FileDropZone({
  onFile,
  disabled,
}: {
  onFile: (file: File) => void;
  disabled?: boolean;
}) {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragActive(true);
      }}
      onDragLeave={() => setDragActive(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragActive(false);
        if (disabled) return;
        const f = e.dataTransfer.files?.[0];
        if (f) onFile(f);
      }}
      className={cn(
        'flex min-h-[160px] flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-8 text-center transition-colors',
        dragActive
          ? 'border-spark bg-spark-soft/40'
          : 'border-border bg-surface',
        disabled ? 'opacity-60' : '',
      )}
    >
      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
        }}
      />
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-spark-soft text-spark">
        <FileGlyph />
      </span>
      <p className="text-sm font-medium text-ink">Drop any file to verify its proof</p>
      <p className="text-xs text-ink-muted">JSON, Markdown, source code, images, DOCX, …</p>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        className={cn(
          'rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink',
          'hover:border-border-strong hover:bg-surface-raised',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-spark',
          'disabled:cursor-not-allowed disabled:opacity-50',
        )}
      >
        Browse file
      </button>
    </div>
  );
}

// ── Proof detail panel ────────────────────────────────────────────

function ProofDetailPanel({
  proof,
  canonical_hash,
  status,
}: {
  proof: EmbeddedProof;
  canonical_hash: string;
  status: ProofStatus;
}) {
  return (
    <div className="flex flex-col gap-3">
      {status === 'OUTDATED' ? (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-amber-300/50 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-700/40 dark:bg-amber-900/20 dark:text-amber-300"
        >
          <span aria-hidden="true" className="mt-0.5 shrink-0">⚠</span>
          <span>File was modified after signing. The proof is structurally valid but the content no longer matches.</span>
        </div>
      ) : null}

      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
        <dt className="font-medium text-ink-muted">Signer</dt>
        <dd className="truncate font-mono text-xs text-ink">{proof.signer}</dd>

        <dt className="font-medium text-ink-muted">Signed at</dt>
        <dd className="text-ink">{proof.timestamp}</dd>

        <dt className="font-medium text-ink-muted">Sequence</dt>
        <dd className="font-mono text-xs text-ink">{proof.sequence}</dd>

        <dt className="font-medium text-ink-muted">Canonical hash</dt>
        <dd>
          <HashDisplay value={canonical_hash as `sha256:${string}`} />
        </dd>
      </dl>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────

export function VerifyFileTab() {
  const { t } = useTranslation('verify');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<VerifyFileResponse | null>(null);
  const [filename, setFilename] = useState<string | null>(null);

  async function handleFile(file: File) {
    setError(null);
    setResult(null);
    setFilename(file.name);
    setLoading(true);
    try {
      const file_b64 = await fileToBase64(file);
      const response = await fetch('/api/v1/proof/verify-file', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_b64, filename: file.name }),
      });
      if (!response.ok) {
        const text = await response.text().catch(() => response.statusText);
        throw new Error(`${response.status}: ${text}`);
      }
      const data = (await response.json()) as VerifyFileResponse;
      setResult(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  const reset = () => {
    setResult(null);
    setError(null);
    setFilename(null);
  };

  return (
    <div className="flex flex-col gap-4">
      <FileDropZone onFile={handleFile} disabled={loading} />

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-4 text-sm text-ink-muted">
          <Spinner size="xs" />
          <span>{t('pending')}</span>
        </div>
      ) : null}

      {error ? (
        <ErrorState
          title="Verification failed"
          body={error}
          onRetry={reset}
        />
      ) : null}

      {result ? (
        <Card>
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <CardTitle>
                {filename ?? 'File'}
              </CardTitle>
              {result.status === 'NONE' ? (
                <CardSubtitle>No embedded proof found in this file.</CardSubtitle>
              ) : null}
            </div>
            <ProofStatusBadge status={result.status} />
          </div>

          {result.proof ? (
            <div className="mt-4">
              <ProofDetailPanel
                proof={result.proof}
                canonical_hash={result.canonical_hash}
                status={result.status}
              />
            </div>
          ) : result.status === 'NONE' ? (
            <p className="mt-3 font-mono text-xs text-ink-subtle">
              canonical_hash: {result.canonical_hash}
            </p>
          ) : null}

          <div className="mt-4 border-t border-border pt-3">
            <button
              type="button"
              onClick={reset}
              className="text-xs text-ink-muted hover:text-ink"
            >
              Verify another file
            </button>
          </div>
        </Card>
      ) : null}
    </div>
  );
}

function FileGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="9" y1="13" x2="15" y2="13" />
      <line x1="9" y1="17" x2="13" y2="17" />
    </svg>
  );
}
